
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import ObjectInputMixin from "@/mixins/ObjectInputMixin.vue";
import "jodit/build/jodit.min.css";
import { JoditEditor } from "jodit-vue";
import DilekceTipPicker from "@/components/pickers/DilekceTipPicker.vue";
import { Udf } from "@/classes/HtmlToUdf";
// @ts-ignore
import html2pdf from "../../../node_modules/html2pdf.js/dist/html2pdf.js";
import { DilekceEntity } from "@/entity/DilekceEntity";

@Component({
  components: { DilekceTipPicker, JoditEditor },
})
export default class DilekceEditor extends Mixins(ObjectInputMixin) {
  @Prop() download!: boolean | string;
  @Prop() error!: boolean;

  specialFieldsStatus = false;
  search = "";
  dilekceAlanlari: Array<DilekceEntity> = [];
  isPreview = false;
  eskiDilekceIsim = "";

  private keyBuster = 0;

  /** Jodit konfigürasyonu (dinamik readonly) */
  get editorConfig() {
    // Lisans kısıtlı dönemde dilekçe indirme/çıktı alma kapalı: yazdır ve
    // UDF/Word/PDF dışa aktarma butonları araç çubuğundan çıkarılır.
    // (Bu butonlar istemci tarafında çalışır, sunucu kapısına takılmaz.)
    const indirmeKisitli = this.$helper.lisansIslemKisitli("dilekce-indirme");
    const cikisButonlari = indirmeKisitli ? [] : ["print", "udf", "word", "pdf"];
    return {
      i18n: "tr",
      language: "tr",
      readonly: !!this.localValue.standart_mi,
      askBeforePasteFromWord: false,
      askBeforePasteHTML: false,
      buttons: [
        "bold",
        "underline",
        "italic",
        "|",
        "fontsize",
        "ul",
        "ol",
        "|",
        "align",
        "table",
        "|",
        "undo",
        "redo",
        "|",
        "onizle",
        ...cikisButonlari,
        "|",
        "alanlar",
      ],
      controls: {
        onizle: { name: "Önizle", exec: () => this.showPreview() },
        udf: { name: "Udf", exec: () => this.saveUdf() },
        pdf: { name: "Pdf", exec: () => this.savePDF() },
        word: { name: "Word", exec: () => this.saveWord() },
        alanlar: { name: "Alanlar", exec: () => (this.specialFieldsStatus = !this.specialFieldsStatus) },
      },
    };
  }

  get isDownload() { return this.download || this.download === ""; }

  get joditKey() {
    const id = this.localValue?.id || "new";
    const name = this.localValue?.isim || "noname";
    // Kısıt durumu (dilekce-indirme) değişirse editör yeniden kurulur ki
    // araç çubuğundaki çıktı butonları açık editörde de güncellensin.
    const kisit = this.$helper.lisansIslemKisitli("dilekce-indirme") ? 1 : 0;
    return `jodit-${id}-${name}-${this.keyBuster}-k${kisit}`;
  }

  get filtrelenmisDilekceAlanlari() {
    const s = this.search.toLowerCase().trim();
    if (!s) return this.dilekceAlanlari;
    return this.dilekceAlanlari.filter((c: any) => c.isim?.toLowerCase?.().includes(s));
  }

  mounted() {
    this.eskiDilekceIsim = this.localValue.isim;
    this.load();
  }

  @Watch("localValue.id") onIdChanged() { this.bumpKey(); }
  @Watch("localValue.isim") onNameChanged() { this.bumpKey(); }

  private bumpKey() {
    this.$nextTick(() => (this.keyBuster += 1));
  }

  /** Yardımcılar */
  showPreview() { this.isPreview = true; }
  focusEditor() {
    (document.getElementsByClassName("jodit-wysiwyg")[0] as HTMLElement)?.focus();
  }
  appendText(item: any) {
    const elem = document.getElementsByClassName("jodit-wysiwyg")[0] as HTMLElement;
    elem?.focus();
    document.execCommand("insertText", false, `{{${item.kod}}}`);
    this.specialFieldsStatus = false;
  }

  findAlanlar() {
    this.localValue.alanlar = [];
    this.dilekceAlanlari.forEach((alan: any) => {
      if (this.localValue.sablon?.includes(`{{${alan.kod}}}`)) this.localValue.alanlar.push(alan.id);
    });
    this.localValue.alanlar = JSON.stringify(this.localValue.alanlar);
    this.input();
  }

  load() {
    this.$http.get("/api/v1/dilekce-alan").then((r: any) => (this.dilekceAlanlari = r.data || []));
  }

  /** Exports */
  saveUdf() { new Udf(this.localValue.sablon, []).generate(this.localValue.isim); }

  saveWord() {
    const header = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8"><title>Save File</title></head><body>`;
    const footer = "</body></html>";
    const sourceHTML = header + (this.localValue.sablon || "") + footer;
    const href = "data:application/vnd.ms-word;charset=utf-8," + encodeURIComponent(sourceHTML);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = href;
    a.download = `${this.localValue.isim || "dilekce"}.doc`;
    a.click();
    document.body.removeChild(a);
    this.$emit("onClose");
  }

  async savePDF() {
    const styled = `
      <div style="
        line-height:1.5;
        font-family:'Times New Roman', serif;
        font-size:12pt;
        white-space:pre-wrap;
        word-break:break-word;
        overflow-wrap:anywhere;
        hyphens:auto;">
        ${(this.localValue.sablon || "").replace(/&nbsp;/g, " ")}
      </div>`;

    await html2pdf()
        .set({
          margin: [15, 15, 20, 15],
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(styled)
        .toPdf()
        .get("pdf")
        .then((pdf: any) => {
          const total = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= total; i++) {
            pdf.setPage(i);
            pdf.text(
                `Sayfa ${i} / ${total}`,
                pdf.internal.pageSize.getWidth() - 30,
                pdf.internal.pageSize.getHeight() - 10
            );
          }
        })
        .save(`${this.localValue.isim || "dilekce"}.pdf`);

    this.$emit("onClose");
  }
}
